import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { SectionCard } from "../../components/ui/SectionCard";
import { FormField, Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, company, updateCompanySettings } = useAuth();

  const [threshold, setThreshold] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initialized && company?.settings) {
      setThreshold(String(company.settings.lateInvoiceThresholdDays ?? 7));
      setInitialized(true);
    }
  }, [company, initialized]);

  if (company && company.type !== "ADMIN") {
    return <Navigate to="/dashboard/invoices" replace />;
  }

  if (user && user.role !== "ADMIN") {
    return <Navigate to="/dashboard/invoices" replace />;
  }

  const handleSave = async () => {
    const value = Number(threshold);
    if (!Number.isInteger(value) || value < 1 || value > 365) {
      setError("settings.late_invoice_threshold_invalid");
      return;
    }
    setError(null);
    setSaving(true);
    setSaved(false);
    try {
      await updateCompanySettings({ lateInvoiceThresholdDays: value });
      setSaved(true);
    } catch (_) {
      setError("settings.save_error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[18px] font-bold text-slate-900">
          {t("settings.page_title")}
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          {t("settings.page_subtitle")}
        </p>
      </div>

      <SectionCard className="max-w-md">
        <FormField
          label={t("settings.late_invoice_threshold_label")}
          helper={t("settings.late_invoice_threshold_helper")}
          error={error ? t(error) : undefined}
        >
          <Input
            type="number"
            min={1}
            max={365}
            value={threshold}
            onChange={(e) => {
              setThreshold(e.target.value);
              setSaved(false);
              setError(null);
            }}
          />
        </FormField>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={handleSave} loading={saving}>
            {t("common.save")}
          </Button>
          {saved && (
            <span className="text-[12.5px] text-emerald-600 font-medium">
              {t("settings.saved_confirmation")}
            </span>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
